import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { CATALOGUE_FILTER, MARKETPLACE_FILTER } from '../../util/catalogueCore';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

/* P25009-410 — the dealer's catalogue, read beside their ads. */
export const QUERY_CATALOGUE_REQUEST = 'app/ProfilePage/QUERY_CATALOGUE_REQUEST';
export const QUERY_CATALOGUE_SUCCESS = 'app/ProfilePage/QUERY_CATALOGUE_SUCCESS';
export const QUERY_CATALOGUE_ERROR = 'app/ProfilePage/QUERY_CATALOGUE_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  catalogueRefs: [],
  catalogueNextPage: null,
  catalogueInProgress: false,
  // The Katalog search in force, kept so "Visa fler" pages the same search.
  catalogueFilter: {},
  /* Whether the store has any catalogue at all — from an unfiltered read —
   * so a search with no hits still shows the section and its "Rensa". */
  catalogueHasAny: false,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };
    case QUERY_CATALOGUE_REQUEST:
      return {
        ...state,
        catalogueInProgress: true,
        // A first page replaces; "Visa fler" appends.
        catalogueRefs: payload.page === 1 ? [] : state.catalogueRefs,
        catalogueFilter: payload.searchParams,
      };
    case QUERY_CATALOGUE_SUCCESS:
      return {
        ...state,
        catalogueInProgress: false,
        catalogueRefs: [...state.catalogueRefs, ...payload.listingRefs],
        catalogueNextPage: payload.nextPage,
        catalogueHasAny:
          state.catalogueHasAny ||
          (Object.keys(state.catalogueFilter || {}).length === 0 && payload.listingRefs.length > 0),
      };
    case QUERY_CATALOGUE_ERROR:
      /* An extra on the profile: a failed read leaves the section out
       * rather than taking the page down with it. */
      return { ...state, catalogueInProgress: false, catalogueNextPage: null };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryUserListings = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      author_id: userId,
      include: ['author', 'images'],
      'pub_isDeleted': false,
      // P25009-410 — the store's ads; its catalogue is read separately below.
      ...MARKETPLACE_FILTER,
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

/* Search within a single seller's listings, in-place on the profile.
 * Reuses the same author_id scoping + image config as queryUserListings,
 * but appends keyword/pub_* filters and updates the same userListingRefs
 * so the profile grid refreshes without navigating to the SearchPage. */
export const searchUserListings = (userId, searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      author_id: userId,
      ...searchParams,
      include: ['author', 'images'],
      'pub_isDeleted': false,
      ...MARKETPLACE_FILTER,
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

/* P25009-410 BR-3 — the dealer's Katalog: stock they have not advertised.
 * Catalogue rows are ordinary published listings kept out of marketplace
 * search by the `visibility` filter, so this is the same author query as the
 * ads above with the opposite filter, one page at a time. */
export const CATALOGUE_PAGE_SIZE = 24;

export const queryUserCatalogue = (userId, config, page = 1, searchParams = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch({ type: QUERY_CATALOGUE_REQUEST, payload: { page, searchParams } });

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      author_id: userId,
      // The buyer's search first, so it can never widen the catalogue filter.
      ...searchParams,
      ...CATALOGUE_FILTER,
      pub_isDeleted: false,
      pub_isHide: false,
      page,
      perPage: CATALOGUE_PAGE_SIZE,
      include: ['author', 'images', 'currentStock'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      const totalPages = response.data.meta?.totalPages || 1;
      dispatch(addMarketplaceEntities(response));
      dispatch({
        type: QUERY_CATALOGUE_SUCCESS,
        payload: { listingRefs, nextPage: page < totalPages ? page + 1 : null },
      });
      return response;
    })
    .catch(e => dispatch({ type: QUERY_CATALOGUE_ERROR, error: true, payload: storableError(e) }));
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess(reviews));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

export const showUser = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(showUser(userId, config)),
    dispatch(queryUserListings(userId, config)),
    dispatch(queryUserCatalogue(userId, config)),
    dispatch(queryUserReviews(userId)),
  ]);
};
